/**
 * RaceWeekSection — v16.3 §G.2 + v16.4 §1: sección dedicada para la
 * `race_week` (gap-fill) entre el fin del taper regular y race_date.
 *
 * Backend §G.1 añade una TrainingWeek con phase="race_week" al final del
 * plan cuando race_date NO cae en lunes (gap >= 1). Contiene de 1 a 6
 * sesiones (rest los días intermedios + activacion_pre_carrera el día
 * previo a la carrera).
 *
 * El componente se renderiza ENTRE el WeekView/WeekNavigator (que solo
 * navega weeks REGULARES — la race_week se excluye de `regularWeeks` en
 * PlanPage) y la RaceDayCard. Sin numeración de semana (no es una
 * semana de entrenamiento canónica), solo header destacado.
 *
 * Caso gap=0 (race_date=lunes): NO existe race_week — la activación
 * sobreescribe el último domingo del taper en su TrainingWeek regular,
 * que se ve dentro del flujo normal de WeekView (sin sección aparte).
 */
import { WeekView } from "@/components/training/WeekView";
import { t } from "@/i18n/es";
import type { TrainingWeek } from "@/types/api";

export interface RaceWeekSectionProps {
  week: TrainingWeek;
  /** v14 §3: pasado tal cual a WeekView para el gate de render del
   *  ritmo /km. Heredado del flujo principal de PlanPage. */
  hasWatch?: boolean;
  /** F3 (s51): exportación FIT — pasa a WeekView (activación exportable). */
  fitExport?: "enabled" | "needs_vam";
  onSessionUpdated?: () => void;
}

export function RaceWeekSection({ week, hasWatch, fitExport, onSessionUpdated }: RaceWeekSectionProps) {
  return (
    <section
      data-testid="race-week-section"
      aria-label={t.plan.raceWeekSectionTitle}
      className="flex flex-col gap-3 rounded-lg border border-lime/30 bg-lime/5 p-4"
    >
      <header className="flex flex-col gap-1">
        <h2 className="text-sm font-semibold uppercase tracking-widest text-lime">
          {t.plan.raceWeekSectionTitle}
        </h2>
        <p className="text-xs text-bone-muted">
          {t.plan.raceWeekSectionSubtitle}
        </p>
      </header>
      {week.sessions.length > 0 && (
        <WeekView week={week} hasWatch={hasWatch} fitExport={fitExport} onSessionUpdated={onSessionUpdated} />
      )}
    </section>
  );
}
